import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CompareHelper } from '@barthauer/abc/core';
import { ResourceManagerService } from '@barthauer/abc/core/resources';
import { DialogResult } from '@barthauer/abc/messages';
import { BasysMapFrameService, PanelComponent } from '@barthauer/basys/map-frame';
import { ResourceSet } from 'libs/abc/core/resources/src/lib/resource-set';
import { AppService } from '../../services/app.service';
import { DataService } from '../../services/data.service';
import { FrameService } from '../../services/frame.service';
import { OkDialogComponent } from '../../shared/ok-dialog.component';
import { WorkConstants, WorkNavigationContext, WorkTypeDurationMode } from '../works.model';
import { WorksService } from '../works.service';
import { DxValidationGroupComponent } from 'devextreme-angular';

@Component({
  selector: 'app-work-detail',
  templateUrl: './work-detail.component.html',
  styleUrls: ['./work-detail.component.css'],
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false },
    },
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class WorkDetailComponent extends PanelComponent implements OnInit, OnDestroy {
  public res = ResourceSet.Empty;
  @ViewChild('stepper', { static: false }) stepper: any; //any is wanted, not MatStepper
  @ViewChild('valiGroupDuration') valiGroupDuration: DxValidationGroupComponent;
  public scrollViewPortElementName: string = 'scrollArea';
  public workNavigationContext: WorkNavigationContext;
  public needForPersistence: boolean;
  public AppService = AppService;
  protected WorkConstants = WorkConstants;
  protected WorkTypeDurationMode = WorkTypeDurationMode;
  public allowDeletion: boolean = false;
  public showResourceToggleButton: boolean = false;
  public selectedTaskIndex: number = 0;
  private isOkDialogOpened: boolean = false;

  constructor(
    public worksService: WorksService,
    private appService: AppService,
    private resManService: ResourceManagerService,
    private dataService: DataService,
    public frame: BasysMapFrameService,
    private frameService: FrameService,
    public changeDetectorRef: ChangeDetectorRef,
    private okDialog: MatDialog,
  ) {
    super();
    super.init(frame);
    this.subscriptions.push(
      this.worksService.onNavigationContextRefresh().subscribe((context) => {
        this.workNavigationContext = context;
        if (this.workNavigationContext.work?.tasks?.length > 0) {
          if (
            (this.workNavigationContext.taskIndex === undefined &&
              this.workNavigationContext.sourceRoute?.designation !== AppService.Route_TaskDetail) ||
            (!this.stepper?.steps &&
              this.workNavigationContext.sourceRoute?.designation === AppService.Route_TaskDetail)
          ) {
            this.workNavigationContext.taskIndex =
              this.workNavigationContext.taskIndex !== undefined ? this.workNavigationContext.taskIndex : -1;
          }

          const firstUnfinishedTaskIndex = this.getFirstUnfinishedTaskIndex();

          requestAnimationFrame(() => {
            this.goToStep(firstUnfinishedTaskIndex);
          });
        } else if (
          this.isEmptyTasks(
            this.workNavigationContext.workDataContext.dataSource.data,
            this.workNavigationContext.workIndex,
          )
        ) {
          this.worksService.navigateTo(AppService.Route_WorkList);
        }

        this.allowDeletion = this.workNavigationContext?.work ? this.workNavigationContext.work.allowDeletion : false;
        if (this.workNavigationContext?.validateDurationOnOpen) {
          requestAnimationFrame(() => {
            this.triggerDurationValidation();
            this.workNavigationContext.validateDurationOnOpen = false;
          });
        }
      }),
    );

    this.subscriptions.push(
      this.dataService.pendingObjectsChanged$.subscribe((poc) => {
        if (poc.category === WorkConstants.PersistenceCategory_WorkDetail) {
          this.needForPersistence = poc.hasAnyChanges;
          changeDetectorRef.detectChanges();
        }
      }),
    );

    this.frameService.bottomSheetToggleCondition = (target: string) => this.ensureCurrentRequiredFieldsFilledIn(target);
  }

  async ngOnInit() {
    super.ngOnInit();
    super.updateSize();
    this.showResourceToggleButton = await this.dataService.hasMtResources();
    this.res = await this.resManService.getResourceSet('abc-maintenance-opera');
  }

  protected ensureCurrentRequiredFieldsFilledIn(target?: string): boolean {
    if (this.isRequiredDurationMissing(target)) {
      this.triggerDurationValidation();
      this.showMissingCurrentRequiredFieldsPopup();
      return false;
    }

    return (
      this.workNavigationContext?.work?.workTypeDurationModeValue === WorkTypeDurationMode.NichtRelevant ||
      this.workNavigationContext?.work?.workTypeDurationModeValue === WorkTypeDurationMode.Optional ||
      target === AppService.Route_TaskDetail ||
      !CompareHelper.isNullorFalse(this.workNavigationContext?.work?.duration) ||
      this.valiGroupDuration?.instance.validate().isValid
    );
  }

  private isRequiredDurationMissing(target?: string): boolean {
    return (
      this.workNavigationContext?.work?.workTypeDurationModeValue === WorkTypeDurationMode.Erforderlich &&
      target !== AppService.Route_TaskDetail &&
      CompareHelper.isNullorFalse(this.workNavigationContext?.work?.duration)
    );
  }

  private triggerDurationValidation(): void {
    this.valiGroupDuration?.instance.validate();
  }

  private async showMissingCurrentRequiredFieldsPopup(): Promise<void> {
    if (this.okDialog.openDialogs.some((dialogRef) => dialogRef.componentInstance instanceof OkDialogComponent)) {
      return;
    }

    if (this.isOkDialogOpened) {
      return;
    }

    const dialogRef = this.okDialog.open(OkDialogComponent, {
      hasBackdrop: false,
      disableClose: true,
    });
    this.isOkDialogOpened = true;

    const okDialog: OkDialogComponent = dialogRef.componentInstance as OkDialogComponent;
    okDialog.onOkClick = () => {
      dialogRef.close();
      this.isOkDialogOpened = false;
    };
    okDialog.dialogHeader = this.res.get('Works-Task-Detail-MissingFieldsHeader');
    okDialog.dialogText =
      this.res.get('Works-Task-Detail-MissingFieldsText') +
      '<ul><li>' +
      this.res.get('Works-Detail-Duration') +
      '</li></ul>';

    await dialogRef.afterClosed().toPromise();
    this.isOkDialogOpened = false;
  }

  public save(): void {
    if (this.ensureCurrentRequiredFieldsFilledIn()) {
      this.appService.callDisplayLoader(true);
      this.dataService.persistPendingChanges(
        WorkConstants.PersistenceCategory_WorkDetail,
        (err) => (this.needForPersistence = true),
      );
      this.needForPersistence = false;
      this.appService.callDisplayLoader(false);
      this.appService.refreshWorksBadgeOnBottombar();
      this.frame.map.clearSelections();
    }
  }

  public async delete(silent?: boolean): Promise<void> {
    let result: DialogResult = DialogResult.Yes;
    if (!silent) {
      const msgQuestionDelete = (await this.res).get('Work-ConfirmDeletionMessage');
      result = await this.frameService.showMessageBox(msgQuestionDelete);
    }

    if (result === DialogResult.Yes) {
      const work = this.workNavigationContext.work;
      if (work) {
        try {
          work.remove();
          this.save();
          await this.frameService.removeWorkFromList(work);
          this.worksService.navigateTo(AppService.Route_WorkList);
        } catch (err) {
          this.frame.error.publish(err);
        }
      }
    }
  }

  public revert(): void {
    this.appService.callDisplayLoader(true);
    this.dataService.revertPendingChanges(WorkConstants.PersistenceCategory_WorkDetail);
    this.needForPersistence = false;
    this.appService.callDisplayLoader(false);
  }

  public completeAll(): void {
    const tasks = this.workNavigationContext?.work?.tasks;
    if (tasks) {
      let save = false;
      for (const t of tasks) {
        if (!t.finished) {
          t.ensureResultsAreLoaded();
          if (t.allResults) {
            for (const r of t.allResults) {
              if (t.isSelectedByAutoComplete(r.mtResultType)) {
                r.selected = true;
              }
            }
          }
          t.finished = true;
          if (t.finished) {
            save = true;
          }
        }
      }
      if (save) {
        this.appService.dataService.persistPendingChanges(
          WorkConstants.PersistenceCategory_TaskDetail,
          (err) => (this.needForPersistence = true),
        );
        this.save();
      }
    }
  }

  public get allowPersistence(): boolean {
    return !this.worksService.isResourceListVisible && this.needForPersistence;
  }

  public get allowCompleteAll(): boolean {
    const tasks = this.workNavigationContext?.work?.tasks;
    return (
      !this.worksService.isResourceListVisible &&
      tasks &&
      !tasks.every((t) => t.finished) &&
      tasks.every((t) => {
        if (t.finished || !t.resultsRequired) {
          return true;
        } else {
          t.ensureResultsAreLoaded();
          return t.allResults?.find((r) => t.isSelectedByAutoComplete(r.mtResultType));
        }
      })
    );
  }

  public get showCompleteAll(): boolean {
    const tasks = this.workNavigationContext?.work?.tasks;
    return (
      tasks &&
      tasks.findIndex((t) => {
        t.ensureResultsAreLoaded();
        return t.allResults?.find((r) => t.isSelectedByAutoComplete(r.mtResultType));
      }) >= 0
    );
  }

  public trackById(index, item) {
    return item.isVirtual ? item.virtualDesignation : item.id;
  }

  ngOnDestroy() {
    console.log('WorkDetailComponent_destroy');
    super.ngOnDestroy();
    this.frameService.bottomSheetToggleCondition = undefined;
  }

  private isEmptyTasks(works: object, workIndex: number): boolean {
    if (Object.keys(works).length < workIndex + 1) {
      if (works[workIndex] && CompareHelper.isEmptyObject(works[workIndex].tasks)) {
        workIndex++;
        this.isEmptyTasks(works, workIndex);
      } else {
        return false;
      }
    } else {
      return true;
    }
  }

  public navigateToTaskDetailWithAutoOpen(index: number): void {
    this.worksService.navigateTo(AppService.Route_TaskDetail, index, true);
  }

  private getFirstUnfinishedTaskIndex(): number {
    const visibleTasks = this.workNavigationContext?.work?.tasks?.filter((task) => task.isVisible) ?? [];
    const firstUnfinishedTaskIndex = visibleTasks.findIndex((task) => !task.finished);

    return firstUnfinishedTaskIndex >= 0 ? firstUnfinishedTaskIndex : 0;
  }

  private goToStep(index: number): void {
    this.selectedTaskIndex = index;

    setTimeout(() => {
      if (this.stepper) {
        this.stepper.selectedIndex = index;
        requestAnimationFrame(() => {
          this.scrollToActiveStep(index);
        });
      }
    });
  }

  private scrollToActiveStep(index: number): void {
    const stepperElement = this.stepper?._elementRef?.nativeElement;
    const headers = stepperElement.querySelectorAll('.mat-step-header');
    const activeHeader = headers[index];

    activeHeader.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }
}
